import {
	BeBox,
	BeBufferBar,
	BeGrow,
	BeKnobSwitch,
	BeMediaSlider,
	BeMenuBar,
	BeMenuBarItem,
	BeRow,
	BeStack,
	BeTransportButton,
	BeVolumeSlider,
	BeWindow,
} from "@beos-css/react/index.ts";
import { type ChangeEvent, type ReactNode, type RefObject, useEffect, useRef, useState } from "react";
import upperMp4 from "../../../../vendored_media/r5/_packages_/Media/optional/movies/Memorial Day (Upper).mp4";
import upperWebm from "../../../../vendored_media/r5/_packages_/Media/optional/movies/Memorial Day (Upper).webm";
import voidMp3 from "../../../../vendored_media/r5/_packages_/Media/optional/sound/MP3/virtual (void).mp3";
import voidOpus from "../../../../vendored_media/r5/_packages_/Media/optional/sound/MP3/virtual (void).opus";

type Mode = "loaded" | "empty" | "stream";

type Playback = {
	position: number;
	duration: number;
	playState: "stopped" | "playing" | "paused";
	onPlayPause: () => void;
	onStop: () => void;
	onZoom: () => void;
	onSeek: (e: ChangeEvent<HTMLInputElement>) => void;
	onVolume: (e: ChangeEvent<HTMLInputElement>) => void;
};

/** Follows a media element: its playhead, its length, and whether it is playing. The
 * element ships with native `controls`; taking them over is this hook's job. */
function usePlayback(media: RefObject<HTMLMediaElement | null>): Playback {
	const [position, setPosition] = useState(0);
	const [duration, setDuration] = useState(0);
	const [playState, setPlayState] = useState<Playback["playState"]>("stopped");

	useEffect(() => {
		const el = media.current;
		if (!el) return;
		el.controls = false;
		const sync = () => {
			setPosition(el.currentTime);
			setDuration(Number.isFinite(el.duration) ? el.duration : 0);
			setPlayState(!el.paused ? "playing" : el.currentTime > 0 && !el.ended ? "paused" : "stopped");
		};
		const events = ["timeupdate", "loadedmetadata", "play", "pause", "ended"];
		for (const e of events) el.addEventListener(e, sync);
		return () => {
			for (const e of events) el.removeEventListener(e, sync);
		};
	}, [media]);

	// full screen: the strip hides after a quiet spell of playback, and any input brings it back
	useEffect(() => {
		const frame = media.current?.closest<HTMLElement>(".be-media-frame");
		if (!frame) return;
		let timer = 0;
		const wake = () => {
			frame.classList.remove("is-idle");
			clearTimeout(timer);
			if (document.fullscreenElement !== frame) return;
			timer = window.setTimeout(() => {
				if (!media.current?.paused) frame.classList.add("is-idle");
			}, 3000);
		};
		const events = ["pointermove", "pointerdown", "keydown", "fullscreenchange"];
		for (const e of events) frame.addEventListener(e, wake);
		return () => {
			clearTimeout(timer);
			for (const e of events) frame.removeEventListener(e, wake);
		};
	}, [media]);

	const onPlayPause = () => {
		const el = media.current;
		if (!el) return;
		if (el.paused) void el.play();
		else el.pause();
	};

	const onStop = () => {
		const el = media.current;
		if (!el) return;
		el.pause();
		el.currentTime = 0;
	};
	const onZoom = () => {
		if (document.fullscreenElement) void document.exitFullscreen();
		else void media.current?.closest(".be-media-frame")?.requestFullscreen();
	};
	const onSeek = (e: ChangeEvent<HTMLInputElement>) => {
		if (media.current) media.current.currentTime = e.currentTarget.valueAsNumber;
	};
	const onVolume = (e: ChangeEvent<HTMLInputElement>) => {
		if (media.current) media.current.volume = e.currentTarget.valueAsNumber / 100;
	};

	return { position, duration, playState, onPlayPause, onStop, onZoom, onSeek, onVolume };
}

// LargeTransportView: the seek bar (a stream's buffer bar) with the twistie, over the buttons and volume.
// A movie's view sits above it, as PlayerWindow stacks the two.
function MediaPlayerWindow({ title, mode, playback, view }: { title: string; mode: Mode; playback?: Playback; view?: ReactNode }) {
	const noFile = mode === "empty";
	const stream = mode === "stream";
	const position = playback?.position ?? 29.00048;
	const duration = playback?.duration ?? 214;
	return (
		<BeWindow title={title} onClose={() => {}} onZoom={() => {}} flush contentStyle={{ width: 321 }}>
			<BeStack gap="none">
				<BeMenuBar aria-label="MediaPlayer">
					<BeMenuBarItem>File</BeMenuBarItem>
					<BeMenuBarItem>View</BeMenuBarItem>
					<BeMenuBarItem>Settings</BeMenuBarItem>
				</BeMenuBar>
				<figure className="be-media-frame">
					{view}
					<BeBox border="plain" flush>
						<BeStack pad="tight" gap="hair">
							<BeRow gap="item" align="end">
								<BeGrow>
									{stream ? (
										<BeBufferBar fluid value={60} />
									) : (
										<BeMediaSlider fluid duration={duration} position={position} disabled={noFile} onChange={playback?.onSeek} />
									)}
								</BeGrow>
								<BeKnobSwitch on />
							</BeRow>
							<BeRow gap="item" align="center">
								<BeRow gap="hair">
									<BeTransportButton kind="rewind" disabled={noFile || stream} />
									<BeTransportButton kind="stop" disabled={noFile} onClick={playback?.onStop} />
									<BeTransportButton
										kind="play"
										playState={playback?.playState ?? (noFile ? "stopped" : "playing")}
										disabled={noFile}
										onClick={playback?.onPlayPause}
									/>
									<BeTransportButton kind="forward" disabled={noFile || stream} />
									<BeTransportButton kind="zoom" disabled={!view} onClick={playback?.onZoom} />
								</BeRow>
								<BeVolumeSlider defaultValue={100} disabled={noFile} onChange={playback?.onVolume} />
							</BeRow>
						</BeStack>
					</BeBox>
				</figure>
			</BeStack>
		</BeWindow>
	);
}

function MoviePlayer() {
	const video = useRef<HTMLVideoElement>(null);
	const playback = usePlayback(video);
	const view = (
		<>
			{/* biome-ignore lint/a11y/useMediaCaption: no caption track exists for this clip yet */}
			<video ref={video} controls preload="metadata" width={320} height={240}>
				<source src={upperWebm} type='video/webm; codecs="av01.0.00M.08, opus"' />
				<source src={upperMp4} type='video/mp4; codecs="avc1.64000D, mp4a.40.2"' />
			</video>
		</>
	);
	return <MediaPlayerWindow title="Memorial Day (Upper)" mode="loaded" playback={playback} view={view} />;
}

function SongPlayer() {
	const audio = useRef<HTMLAudioElement>(null);
	const playback = usePlayback(audio);
	return (
		<>
			{/* biome-ignore lint/a11y/useMediaCaption: no transcript exists for this track yet */}
			<audio ref={audio} controls preload="metadata">
				<source src={voidOpus} type='audio/ogg; codecs="opus"' />
				<source src={voidMp3} type="audio/mpeg" />
			</audio>
			<MediaPlayerWindow title="virtual (void)" mode="loaded" playback={playback} />
		</>
	);
}

export function MediaPlayerDemo() {
	return (
		<section id="demo-media-player">
			<h2>MediaPlayer (TransportView)</h2>
			<p>
				MediaPlayer's Large mode playing a movie and a song from the BeOS R5 disc, then with no file and on a stream. Press Play to start or
				pause either file; the seek bar and time label follow along.
			</p>
			<BeRow wrap gap="row" align="start">
				<MoviePlayer />
				<SongPlayer />
				<MediaPlayerWindow title="MediaPlayer" mode="empty" />
				<MediaPlayerWindow title="KBeR Radio" mode="stream" />
			</BeRow>
		</section>
	);
}
